import React, { useState, useCallback, useMemo } from "react";
import { 
  FaDatabase, 
  FaBullseye, 
  FaShareAlt, 
  FaGavel, 
  FaLock, 
  FaCookieBite, 
  FaGlobeAmericas, 
  FaSyncAlt, 
  FaHeadset,
  FaShieldAlt,
  FaCalendarAlt,
  FaEnvelope,
  FaPhone,
  FaInfoCircle,
  FaUserShield,
  FaChevronDown,
  FaChevronRight,
  FaPrint,
  FaMapMarkerAlt,
  FaExclamationTriangle,
  FaCheckCircle,
  FaEye
} from "react-icons/fa";
import { COMPANY_INFO } from '../constants';
import Seo from './common/Seo';

// Datos estructurados de las secciones
const SECTIONS_DATA = [
  {
    id: "introduction",
    icon: FaInfoCircle,
    title: "Aviso de Privacidad Simplificado",
    content: `Este Aviso forma parte del uso de nuestros servicios y sitio web. Para ${COMPANY_INFO.legalName}, denominada "La Empresa", es responsable del tratamiento de sus datos personales, con domicilio en ${COMPANY_INFO.address}.`,
    highlight: true
  },
  {
    id: "data-collection",
    icon: FaDatabase,
    title: "¿Para qué recabamos y utilizamos sus datos?",
    content: "Con la finalidad de poder otorgar un mejor servicio de enfermería especializada y brindar información de los servicios que ofrecemos, requerimos:",
    list: [
      "Nombre(s) y Apellidos del paciente y familiar responsable",
      "Teléfono de contacto principal y de emergencia", 
      "Dirección completa para servicios a domicilio",
      "Correo electrónico para comunicaciones",
      "Datos médicos relevantes (diagnósticos, medicamentos, alergias)",
      "Información sobre necesidades específicas de cuidado",
      "Datos del médico tratante y contactos de emergencia"
    ],
    note: "Para dar un buen servicio requerimos que la información deberá ser veraz y completa en todo momento. El usuario podrá completar los campos para recabar la información solicitada y, en ningún caso 'La Empresa' será responsable de información falsa o incompleta."
  },
  {
    id: "data-usage",
    icon: FaBullseye,
    title: "Uso de la Información",
    content: "La Empresa utiliza la información obtenida para:",
    list: [
      "Contactar al usuario y proporcionar servicios de enfermería eficientes",
      "Coordinar citas médicas y seguimiento del paciente",
      "Envío de información sobre nuestros servicios y promociones",
      "Comunicación con médicos tratantes y personal de salud autorizado",
      "Facturación y gestión administrativa de los servicios",
      "Mejora continua de nuestros protocolos de atención",
      "Cumplimiento de obligaciones legales del sector salud"
    ]
  },
  {
    id: "ip-analytics",
    icon: FaEye,
    title: "Información Técnica y Analítica",
    content: "La Empresa utiliza información de la dirección IP para analizar y tener una mejor administración del sitio web. La IP no será utilizada para identificar a los usuarios, siempre y cuando no se esté investigando un caso de posibles actividades fraudulentas o uso indebido de nuestros servicios."
  },
  {
    id: "data-processing",
    icon: FaSyncAlt,
    title: "Procesamiento de la Información",
    content: "Una vez recibida la información, ésta es procesada para permanecer en nuestra base de datos segura, posteriormente se turna a personal médico especializado para:",
    list: [
      "Evaluar las necesidades específicas del paciente",
      "Asignar al profesional de enfermería más adecuado",
      "Establecer protocolos de atención personalizada",
      "Coordinar con el equipo médico multidisciplinario",
      "Generar reportes de seguimiento médico"
    ]
  },
  {
    id: "security-measures",
    icon: FaLock,
    title: "Medidas de Protección",
    content: "La Empresa incorpora mecanismos de protección técnicos, físicos y administrativos, a fin de evitar razonablemente:",
    list: [
      "Desviaciones o uso no autorizado de datos médicos",
      "Adulteración de historiales clínicos",
      "Pérdida de información crítica del paciente",
      "Consulta no autorizada por personal no médico",
      "Tratamiento inadecuado de datos sensibles de salud"
    ]
  },
  {
    id: "data-sharing",
    icon: FaShareAlt,
    title: "Compartición de Información",
    content: "La Empresa no venderá, cederá ni transferirá la información obtenida. La Empresa no revelará información a terceros salvo cuando:",
    list: [
      "El usuario o familiar responsable lo autorice por escrito",
      "Sea requerido por autoridades de salud competentes",
      "Sea necesario para la atención médica de emergencia",
      "Se requiera para cumplir con obligaciones legales del sector salud",
      "Sea indispensable para la coordinación con otros profesionales médicos autorizados"
    ]
  },
  {
    id: "data-protection-implementation",
    icon: FaShieldAlt,
    title: "Implementación de Seguridad",
    content: "La Empresa implementa las herramientas necesarias para la protección de la información médica y personal del usuario:",
    list: [
      "Sistemas de encriptación para datos sensibles",
      "Acceso restringido solo a personal médico autorizado",
      "Protocolos de confidencialidad firmados por empleados",
      "Respaldos seguros de información médica crítica",
      "Auditorías periódicas de seguridad de datos"
    ],
    note: "Una vez recibidos los datos, haremos todo lo posible para salvaguardar su seguridad en nuestro sistema siguiendo las mejores prácticas del sector salud."
  },
  {
    id: "user-rights",
    icon: FaGavel,
    title: "Derechos del Usuario",
    content: "El usuario en carácter de titular de la información, podrá solicitar al responsable de datos:",
    list: [
      "Acceder a sus datos personales y médicos incluidos en nuestra base",
      "Rectificar o actualizar su información médica en cualquier momento",
      "Cancelar datos que no sean necesarios para su atención",
      "Oponerse al tratamiento de datos para fines secundarios",
      "Revocar su consentimiento para el tratamiento de datos sensibles"
    ]
  },
  {
    id: "cookies-web",
    icon: FaCookieBite,
    title: "Cookies y Tecnologías Web",
    content: "Nuestro sitio web utiliza cookies y tecnologías similares para:",
    list: [
      "Cookies técnicas esenciales para el funcionamiento básico",
      "Cookies de sesión para mantener la seguridad de acceso",
      "Google Analytics para analizar el tráfico (datos anónimos)",
      "Cookies de preferencias para mejorar la experiencia del usuario"
    ],
    note: "Puede desactivar las cookies desde la configuración de su navegador, aunque esto podría afectar algunas funcionalidades del sitio."
  },
  {
    id: "policy-modifications",
    icon: FaCalendarAlt,
    title: "Modificaciones de la Política de Privacidad",
    content: "Nos reservamos el derecho de efectuar en cualquier momento, modificaciones en la presente Política de Privacidad y adaptarla a:",
    list: [
      "Novedades legislativas en materia de protección de datos",
      "Cambios en jurisprudencia del sector salud",
      "Mejores prácticas del mercado médico",
      "Actualizaciones en tecnologías de protección de datos"
    ],
    note: "Queda bajo responsabilidad del usuario leer periódicamente las Políticas de Privacidad para estar al tanto de posibles modificaciones. Una vez introducida en el sitio Web, la modificación entrará automáticamente en vigencia."
  }
];

// Hook personalizado para manejar secciones colapsables
const useCollapsibleSections = (initialState = {}) => {
  const [expandedSections, setExpandedSections] = useState(initialState);

  const toggleSection = useCallback((sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  }, []);

  const expandAll = useCallback(() => {
    const allExpanded = SECTIONS_DATA.reduce((acc, section) => ({
      ...acc,
      [section.id]: true
    }), {});
    setExpandedSections(allExpanded);
  }, []);

  const collapseAll = useCallback(() => {
    setExpandedSections({});
  }, []);

  return { expandedSections, toggleSection, expandAll, collapseAll };
};

// Componente de encabezado
const PrivacyHeader = React.memo(() => (
  <header className="relative overflow-hidden">
    <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 rounded-2xl shadow-2xl">
      {/* Patrón de fondo decorativo */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-64 h-64 transform translate-x-32 -translate-y-32 rotate-45 bg-white rounded-full"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 transform -translate-x-24 translate-y-24 rotate-12 bg-white rounded-full"></div>
      </div>
      
      <div className="relative px-6 py-12 text-center text-white">
        <div className="flex flex-col items-center">
          <div className="bg-white/20 backdrop-blur-sm p-6 rounded-full mb-6 shadow-lg">
            <FaShieldAlt className="text-5xl text-white" />
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
            Aviso de Privacidad
          </h1>
          
          <p className="text-xl text-blue-100 max-w-2xl mx-auto mb-6 leading-relaxed">
            {COMPANY_INFO.name} - {COMPANY_INFO.description}
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full flex items-center">
              <FaCalendarAlt className="mr-2" />
              <span>Última actualización: {COMPANY_INFO.lastUpdate}</span>
            </div>
            <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full flex items-center">
              <FaGlobeAmericas className="mr-2" />
              <span>{COMPANY_INFO.website}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </header>
));

PrivacyHeader.displayName = 'PrivacyHeader';

// Componente para controles de sección
const SectionControls = React.memo(({ onExpandAll, onCollapseAll, onPrint }) => (
  <div className="bg-white rounded-xl shadow-md p-4 mb-8 border border-gray-100">
    <div className="flex flex-wrap justify-between items-center gap-4">
      <div className="flex flex-wrap gap-2">
        <button
          onClick={onExpandAll}
          className="flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors duration-200"
          aria-label="Expandir todas las secciones"
        >
          <FaChevronDown className="mr-2" />
          Expandir Todo
        </button>
        <button
          onClick={onCollapseAll}
          className="flex items-center px-4 py-2 text-sm font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          aria-label="Contraer todas las secciones"
        >
          <FaChevronRight className="mr-2" />
          Contraer Todo
        </button>
      </div>
      
      <div className="flex gap-2">
        <button
          onClick={onPrint}
          className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg transition-colors duration-200"
          aria-label="Imprimir aviso de privacidad"
        >
          <FaPrint className="mr-2" />
          Imprimir
        </button>
      </div>
    </div>
  </div>
));

SectionControls.displayName = 'SectionControls';

// Componente de sección mejorado
const Section = React.memo(({ 
  section, 
  isExpanded, 
  onToggle 
}) => {
  const { id, icon: IconComponent, title, content, list, note, highlight } = section;
  
  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle(id);
    }
  }, [id, onToggle]);

  return (
    <article className={`border-b border-gray-100 last:border-b-0 transition-all duration-300 ${highlight ? 'bg-blue-50/50' : ''}`}>
      <button
        onClick={() => onToggle(id)}
        onKeyDown={handleKeyDown}
        className="w-full p-6 md:p-8 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset transition-colors duration-200"
        aria-expanded={isExpanded}
        aria-controls={`section-content-${id}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center flex-1">
            <div className={`p-3 rounded-xl mr-4 transition-colors duration-200 ${
              isExpanded ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-600'
            }`}>
              <IconComponent className="text-xl" />
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 flex-1">
              {title}
            </h2>
          </div>
          <div className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
            <FaChevronDown className="text-gray-400" />
          </div>
        </div>
      </button>
      
      <div
        id={`section-content-${id}`}
        className={`overflow-hidden transition-all duration-300 ${
          isExpanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="pl-6 pr-6 pb-6 md:pl-20 md:pr-8 md:pb-8">
          <div className="prose prose-gray max-w-none">
            <p className="text-gray-700 leading-relaxed mb-4">{content}</p>
            
            {list && (
              <ul className="space-y-3 mt-4">
                {list.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start">
                    <FaCheckCircle className="text-blue-500 mt-1 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            )}
            
            {note && (
              <div className="mt-6 p-4 bg-amber-50 border-l-4 border-amber-400 rounded-r-lg">
                <div className="flex">
                  <FaExclamationTriangle className="text-amber-600 mt-1 mr-3 flex-shrink-0" />
                  <p className="text-amber-800 text-sm">
                    <strong>Nota importante:</strong> {note}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
});

Section.displayName = 'Section';

// Componente de información de contacto
const ContactSection = React.memo(() => (
  <section className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl shadow-xl text-white overflow-hidden">
    <div className="relative p-8">
      <div className="absolute top-0 right-0 opacity-10">
        <div className="w-32 h-32 transform rotate-45 bg-white rounded-lg"></div>
      </div>
      
      <div className="relative">
        <div className="flex items-center mb-6">
          <div className="bg-white/20 p-3 rounded-lg mr-4">
            <FaHeadset className="text-2xl text-white" />
          </div>
          <h2 className="text-2xl font-bold">Contacto y Más Información</h2>
        </div>
        
        <div className="space-y-6">
          <p className="text-blue-100 text-lg leading-relaxed">
            Para ejercer sus derechos ARCO, aclaraciones sobre este aviso de privacidad, 
            o cualquier duda relacionada con el tratamiento de sus datos personales, 
            puede contactarnos a través de:
          </p>
          
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-colors duration-200">
              <div className="flex items-center space-x-3">
                <FaEnvelope className="text-blue-200 text-xl" />
                <div>
                  <dt className="text-sm font-medium text-blue-200 uppercase tracking-wide">Email</dt>
                  <dd>
                    <a 
                      href={`mailto:${COMPANY_INFO.email}`}
                      className="text-white hover:text-blue-100 transition-colors duration-200"
                    >
                      {COMPANY_INFO.email}
                    </a>
                  </dd>
                </div>
              </div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-colors duration-200">
              <div className="flex items-center space-x-3">
                <FaPhone className="text-blue-200 text-xl" />
                <div>
                  <dt className="text-sm font-medium text-blue-200 uppercase tracking-wide">Teléfono</dt>
                  <dd>
                    <a 
                      href={`tel:${COMPANY_INFO.phone.replace(/\s/g, '')}`}
                      className="text-white hover:text-blue-100 transition-colors duration-200"
                    >
                      {COMPANY_INFO.phone}
                    </a>
                  </dd>
                </div>
              </div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-colors duration-200">
              <div className="flex items-center space-x-3">
                <FaMapMarkerAlt className="text-blue-200 text-xl" />
                <div>
                  <dt className="text-sm font-medium text-blue-200 uppercase tracking-wide">Dirección</dt>
                  <dd className="text-white text-sm">
                    {COMPANY_INFO.address}
                  </dd>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
            <div className="flex items-start space-x-4">
              <FaUserShield className="text-3xl text-blue-200 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-bold mb-3">Compromiso con su Privacidad</h3>
                <p className="text-blue-100 leading-relaxed">
                  En {COMPANY_INFO.name} nos comprometemos a proteger su información personal 
                  y médica con los más altos estándares de seguridad y confidencialidad. 
                  Su confianza es fundamental para brindarle el mejor cuidado especializado.
                </p>
                <p className="text-blue-100 mt-3 text-sm">
                  Si requiere mayor información, puede consultar nuestro aviso de privacidad 
                  completo en nuestra página web <strong>{COMPANY_INFO.website}</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
));

ContactSection.displayName = 'ContactSection';

// Componente principal
const PrivacyPolicy = () => {
  // Estado inicial: primera sección expandida
  const initialExpanded = useMemo(() => ({ introduction: true }), []);
  const { expandedSections, toggleSection, expandAll, collapseAll } = useCollapsibleSections(initialExpanded);

  // Función para imprimir
  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-blue-100 to-gray-50 py-8">
      <Seo
        title="Aviso de Privacidad | Geriatría Serena"
        description="Conoce cómo Geriatría Serena recaba, usa y protege tus datos personales y médicos al contratar servicios de enfermería y cuidado a domicilio en Guadalajara."
        canonical="https://geriatriaserena.com/privacy-policy"
        robots="index, follow"
      />
      <div className="max-w-5xl mx-auto px-4">
        <PrivacyHeader />
        
        <div className="mt-10">
          <SectionControls
            onExpandAll={expandAll}
            onCollapseAll={collapseAll}
            onPrint={handlePrint}
          />
          
          {/* Contenido Principal */}
          <main className="bg-white rounded-2xl shadow-xl overflow-hidden mb-10">
            {SECTIONS_DATA.map((section) => (
              <Section
                key={section.id}
                section={section}
                isExpanded={expandedSections[section.id]}
                onToggle={toggleSection}
              />
            ))}
          </main>
          
          <ContactSection />
        </div>
        
        {/* Footer */}
        <footer className="mt-10 text-center text-gray-500 text-sm space-y-2">
          <p>© 2025 {COMPANY_INFO.name}. Todos los derechos reservados.</p>
          <p>Aviso de Privacidad válido y vigente desde {COMPANY_INFO.lastUpdate}</p>
          <p className="text-xs text-gray-400">
            Este documento cumple con la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP)
          </p>
        </footer>
      </div>
    </div>
  );
};

export default React.memo(PrivacyPolicy);